// Univerzální hlavní menu Phoenixu — lišta s logem, native nav položkami (leadingItems),
// top-level sekcemi (dropdown s drill-down sloupci) a pravým slotem.
// Pixel-perfect port prototypu na Phoenix design systém (--phx-* tokeny).

import { Children, Fragment, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

import { GlobalLoadBadge } from './GlobalLoadBadge';
import { MenuBarButton } from './MenuBarButton';
import { MENU_DENSITY } from './density';
import { menuTypeIcon } from './menuTypeIcon';
import type {
  MenuActionContext,
  MenuDensity,
  MenuIconResolver,
  MenuLoadViz,
  MenuSection,
  MenuSepStyle,
} from './menuTypes';

export interface MainMenuProps {
  /** Top-level sekce (Obecné, Inventář, …) = triggery v liště. */
  readonly sections: readonly MenuSection[];
  /** Hustota řádků. Default `compact`. */
  readonly density?: MenuDensity;
  /** Styl oddělovačů sekcí. Default `lines`. */
  readonly separators?: MenuSepStyle;
  /** Probíhá donačítání legacy agend (před `desktop/menu-ready`). */
  readonly loading?: boolean;
  /** Načítání plochy Magicu selhalo → místo spinneru červená chyba (má přednost před `loading`). */
  readonly loadError?: boolean;
  /** Vizualizace načítání. Default `spinner`. */
  readonly loadViz?: MenuLoadViz;
  /** Tlačítko osamostatnit vždy viditelné (jinak jen na hover). Default `true`. */
  readonly detachAlways?: boolean;
  /** Resolver type-ikony. Default `menuTypeIcon`. */
  readonly iconForNode?: MenuIconResolver;
  /** Klik na listovou agendu — otevřít v ploše (nahradí plochu). */
  readonly onOpen: (ctx: MenuActionContext) => void;
  /** Osamostatnit agendu do plovoucího okna. */
  readonly onDetach: (ctx: MenuActionContext) => void;
  readonly logo?: ReactNode;
  /** Native nav položky vlevo (bez cílové sekce) — vykresluje app. */
  readonly leadingItems?: ReactNode;
  readonly rightContent?: ReactNode;
  /** Barva indikátoru prostředí (DEV/ENV) — podbarví celé menu + zvýrazní spodní hranu. `undefined` = skryto. */
  readonly envTintColor?: string;
  readonly className?: string;
}

export function MainMenu({
  sections,
  density = 'compact',
  separators = 'lines',
  loading = false,
  loadError = false,
  loadViz = 'spinner',
  detachAlways = true,
  iconForNode = menuTypeIcon,
  onOpen,
  onDetach,
  logo,
  leadingItems,
  rightContent,
  envTintColor,
  className,
}: MainMenuProps) {
  const densityTokens = MENU_DENSITY[density];

  // Top-level položky lišty = native nav (leadingItems) + sekce. Mezi nimi „špuntík" (·).
  const entries: { key: string; node: ReactNode }[] = [
    ...Children.toArray(leadingItems).map((node, i) => ({ key: `lead-${i}`, node })),
    ...sections.map((section) => ({
      key: section.id,
      node: (
        <MenuBarButton
          section={section}
          density={density}
          densityTokens={densityTokens}
          separators={separators}
          detachAlways={detachAlways}
          loading={loading}
          loadViz={loadViz}
          iconForNode={iconForNode}
          onOpen={onOpen}
          onDetach={onDetach}
        />
      ),
    })),
  ];

  // Indikátor prostředí (DEV/ENV): podbarví celé menu jemným nádechem env barvy (čitelnost zůstává,
  // text/hover tokeny leží na převážně surface pozadí) a zvýrazní spodní hranu plnou env barvou.
  const envTintStyle = envTintColor
    ? {
        background: `color-mix(in srgb, ${envTintColor} 16%, var(--phx-surface))`,
        borderBottomColor: envTintColor,
      }
    : undefined;

  return (
    <nav className={cn('legacy-menu', className)} data-testid="main-menu" style={envTintStyle}>
      {logo !== undefined && (
        <span className="legacy-menu__logo" data-testid="main-menu-logo">
          {logo}
        </span>
      )}
      <div className="legacy-menu__list">
        {entries.map((entry, i) => (
          <Fragment key={entry.key}>
            {i > 0 && (
              <span
                aria-hidden="true"
                className="text-phx-text-faint text-phx-lg pointer-events-none mx-0.5 shrink-0 self-center leading-none select-none"
              >
                ·
              </span>
            )}
            {entry.node}
          </Fragment>
        ))}
      </div>
      <span className="legacy-menu__spacer" />
      <div className="flex items-center gap-2">
        <GlobalLoadBadge loading={loading} error={loadError} viz={loadViz} />
        {rightContent}
      </div>
    </nav>
  );
}
